<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CampaignsAPI from 'dashboard/api/campaigns';

const { t } = useI18n();

const isLoading = ref(true);
const credits = ref({
  available: 0,
  available_brl: 0,
  total_purchased: 0,
  total_used: 0,
  total_bonus: 0,
});

const balanceInBRL = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(credits.value.available_brl ?? 0)
);

const balanceClass = computed(() => {
  const brl = credits.value.available_brl ?? 0;
  if (brl <= 0) return 'text-n-ruby-11 bg-n-ruby-3';
  if (brl < 10) return 'text-n-amber-11 bg-n-amber-3';
  return 'text-n-teal-11 bg-n-teal-3';
});

const fetchCredits = async () => {
  try {
    isLoading.value = true;
    const response = await CampaignsAPI.getCampaignUsage();
    if (response.data?.credits) {
      credits.value = response.data.credits;
    }
  } catch {
    // silently fail
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchCredits);

defineExpose({ refresh: fetchCredits });
</script>

<template>
  <div
    class="flex items-center gap-3 px-4 py-2 rounded-xl border border-n-weak bg-n-alpha-1"
  >
    <div class="flex items-center gap-2">
      <span class="i-lucide-coins w-4 h-4 text-n-slate-10" />
      <span class="text-sm font-medium text-n-slate-11">
        {{ t('CAMPAIGN.CREDITS.LABEL') }}
      </span>
    </div>
    <div v-if="isLoading" class="flex items-center gap-2">
      <span class="i-lucide-loader-2 w-4 h-4 text-n-slate-10 animate-spin" />
    </div>
    <div v-else class="flex items-center gap-2">
      <span
        :class="balanceClass"
        class="px-2.5 py-1 rounded-lg text-sm font-semibold"
      >
        {{ balanceInBRL }}
      </span>
    </div>
  </div>
</template>
